import React from 'react';

import { useMediaQuery } from '@mui/material';
import { Box } from '@provider-portal/design-common/kalep/components/Box/Box';
import { Drawer } from '@provider-portal/design-common/kalep/components/Drawer/Drawer';
import { Menu } from '@provider-portal/design-common/kalep/components/Menu/Menu';
import { theme } from '@provider-portal/design-common/kalep/theme/styles/themeMui';

import { StoreSelectorContent } from './StoreSelectorContent';
import { StoreSelectorCurrentStore } from './StoreSelectorCurrentStore';
import { Availability, StoreListGroup } from './StoreSelectorStoresList';

export interface StoreSelectorProps {
  parentName: string;
  storeName: string;
  stores: StoreListGroup[];
  storeAvailabilities: Map<number, Availability>;
  storeAvailabilitiesLoading: boolean;
  selectedStoreId?: number;
  showAddVenueButton: boolean;
  onAddVenue?: () => void;
  onSelectStore?: (storeId: number) => void;
  onSelectorOpen?: () => void;
  onSearchPerformed?: (searchQuery: string, resultsCount: number, searchCount: number) => void;
}

export function StoreSelector({
  parentName,
  storeName,
  stores,
  storeAvailabilities,
  storeAvailabilitiesLoading,
  selectedStoreId,
  showAddVenueButton,
  onAddVenue,
  onSelectStore,
  onSelectorOpen,
  onSearchPerformed,
}: StoreSelectorProps) {
  const isMd = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const handleArrowClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);

      // Trigger the optional onSelectorOpen callback
      if (onSelectorOpen) {
        onSelectorOpen();
      }
    },
    [onSelectorOpen],
  );
  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handeAddVenue = React.useCallback(() => {
    if (onAddVenue) {
      onAddVenue();
    }
    setAnchorEl(null);
  }, [onAddVenue]);

  const handleSelectStore = React.useCallback(
    (storeId: number) => {
      if (onSelectStore) {
        onSelectStore(storeId);
      }
      setAnchorEl(null);
    },
    [onSelectStore],
  );

  return (
    <Box gap="8px" display="flex" alignItems="center" width={!isMd ? '100%' : undefined}>
      <StoreSelectorCurrentStore
        isOpen={isOpen}
        storeName={storeName}
        parentName={parentName}
        onClick={handleArrowClick}
      />
      {!isMd ? (
        <Menu
          id="store-selector-menu"
          anchorEl={anchorEl}
          open={isOpen}
          onClose={handleClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{
            paper: {
              sx: {
                minWidth: 320,
                maxWidth: 320,
                height: 556,
                maxHeight: 556,
                // Ensure the paper is the scrolling container
                overflowY: 'auto',
              },
            },
          }}
        >
          <StoreSelectorContent
            selectedStoreId={selectedStoreId}
            stores={stores}
            storeAvailabilities={storeAvailabilities}
            storeAvailabilitiesLoading={storeAvailabilitiesLoading}
            onSelectStore={handleSelectStore}
            showAddVenueButton={showAddVenueButton}
            onAddVenue={handeAddVenue}
            onSearchPerformed={onSearchPerformed}
          />
        </Menu>
      ) : (
        <Drawer
          onClose={handleClose}
          open={isOpen}
          anchor="bottom"
          slotProps={{
            paper: {
              sx: {
                minWidth: 320,
                height: 'calc(100vh - 200px)',
                maxHeight: 556,
                // Ensure the paper is the scrolling container
                overflowY: 'auto',
                boxShadow: 'none !important',
              },
            },
          }}
        >
          <StoreSelectorContent
            selectedStoreId={selectedStoreId}
            stores={stores}
            storeAvailabilities={storeAvailabilities}
            storeAvailabilitiesLoading={storeAvailabilitiesLoading}
            onSelectStore={handleSelectStore}
            onAddVenue={handeAddVenue}
            showAddVenueButton={showAddVenueButton}
            onSearchPerformed={onSearchPerformed}
          />
        </Drawer>
      )}
    </Box>
  );
}
